// Design Proposal v2 — "Tactical High-Frequency Command Desk".
// Pure styling preview — zero logic, zero API calls.  Shown next to
// /styleguide (v1) so the user can pick a direction.
// Route: /styleguide-v2
import { Link } from "react-router-dom";
import {
  Calendar, Target, Repeat, History, Zap, Trophy, Clock, ChevronRight,
  Menu, Search, BarChart3, Activity, ArrowUpRight, ArrowDownRight,
  Signal,
} from "lucide-react";

const PALETTE = [
  { name: "Obsidian",    hex: "#06080d", role: "App background" },
  { name: "Panel",       hex: "#0d121d", role: "Card surface" },
  { name: "Elevated",    hex: "#141b2a", role: "Elevated card" },
  { name: "Hairline",    hex: "#1b2738", role: "Border divider" },
  { name: "Text",        hex: "#f8fafc", role: "Primary text" },
  { name: "Text muted",  hex: "#64748b", role: "Secondary text" },
  { name: "Cyan",        hex: "#0ea5e9", role: "Primary action" },
  { name: "Mint",        hex: "#10b981", role: "Positive / win" },
  { name: "Crimson",     hex: "#ff3b30", role: "Negative / stop" },
  { name: "Amber",       hex: "#f59e0b", role: "Warning" },
];

const NAV = [
  { label: "Schedule",  icon: Calendar, active: true },
  { label: "Live",      icon: Zap,      active: false },
  { label: "Autopilot", icon: Target,   active: false },
  { label: "Rollover",  icon: Repeat,   active: false },
  { label: "Accuracy",  icon: Trophy,   active: false },
];

const PICKS = [
  { home: "Sassuolo", away: "Lazio",    league: "Serie A",    ko: "18:00",
    market: "HT BTTS",       pick: "No",             conf: 74.8, odds: 1.72, delta: +4.1 },
  { home: "AS Roma",  away: "Cagliari", league: "Serie A",    ko: "20:45",
    market: "HT BTTS",       pick: "No",             conf: 81.3, odds: 1.65, delta: +2.6 },
  { home: "Nantes",   away: "Rennes",   league: "Ligue 1",    ko: "20:00",
    market: "Double Chance", pick: "Home or Draw",   conf: 69.0, odds: 1.44, delta: -1.1 },
];

const TABLE_ROWS = [
  { t: "18:00:12", pair: "Sassuolo · Lazio",   mkt: "HT BTTS / No",     stake: 24.00, odds: 1.72, pnl: +16.72, state: "placed"  },
  { t: "18:02:48", pair: "Nantes · Rennes",    mkt: "DC / 1X",          stake: 24.00, odds: 1.44, pnl:   0.00, state: "placing" },
  { t: "17:51:03", pair: "Everton · Newcastle",mkt: "O/U 2.5 / Under",  stake: 20.00, odds: 2.05, pnl: -20.00, state: "failed"  },
  { t: "17:40:15", pair: "AS Roma · Cagliari", mkt: "HT BTTS / No",     stake: 24.00, odds: 1.65, pnl: +15.60, state: "settled" },
];

const STATE_COLORS = {
  placed:  { fg: "#f8fafc", bg: "rgba(14,165,233,0.14)", bd: "rgba(14,165,233,0.35)" },
  placing: { fg: "#f8fafc", bg: "rgba(245,158,11,0.14)", bd: "rgba(245,158,11,0.35)" },
  settled: { fg: "#f8fafc", bg: "rgba(16,185,129,0.14)", bd: "rgba(16,185,129,0.35)" },
  failed:  { fg: "#f8fafc", bg: "rgba(255,59,48,0.14)",  bd: "rgba(255,59,48,0.4)"  },
};

const TILES = [
  { label: "ROI 7d",        value: "+18.4%",   trend: +2.3, pos: true,  icon: ArrowUpRight },
  { label: "Hit rate",      value: "63.2%",    trend: +1.1, pos: true,  icon: Signal       },
  { label: "Avg odds",      value: "1.72",     trend: -0.03,pos: false, icon: Activity     },
  { label: "Open bets",     value: "7",        trend: null, pos: null,  icon: Target       },
];

export default function StyleguideV2Page() {
  return (
    <div
      className="min-h-screen w-full"
      data-testid="styleguide-v2-page"
      style={{
        background:
          "radial-gradient(1000px 500px at 0% 0%, rgba(14,165,233,0.07), transparent 60%)," +
          "radial-gradient(900px 500px at 100% 0%, rgba(16,185,129,0.05), transparent 60%)," +
          "#06080d",
        color: "#f8fafc",
        fontFamily: '"Plus Jakarta Sans", "Inter", ui-sans-serif, system-ui, sans-serif',
      }}
    >
      {/* Fonts */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link
        href="https://fonts.googleapis.com/css2?family=Outfit:wght@500;600;700;900&family=Plus+Jakarta+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap"
        rel="stylesheet"
      />

      {/* ─── v2 ↔ v1 toggle ─── */}
      <div className="w-full flex justify-center pt-4 pb-2">
        <div
          className="inline-flex items-center rounded-full p-1 text-xs"
          style={{
            background: "rgba(13,18,29,0.8)",
            border: "1px solid #1b2738",
            backdropFilter: "blur(14px)",
          }}
          data-testid="styleguide-toggle"
        >
          <Link
            to="/styleguide"
            className="px-4 py-1.5 rounded-full transition"
            style={{ color: "#64748b" }}
            data-testid="styleguide-toggle-v1"
          >
            v1 · Amber
          </Link>
          <span
            className="px-4 py-1.5 rounded-full font-medium"
            style={{
              background: "rgba(14,165,233,0.14)",
              color: "#0ea5e9",
              border: "1px solid rgba(14,165,233,0.35)",
              fontFamily: '"IBM Plex Mono", monospace',
            }}
            data-testid="styleguide-toggle-v2-current"
          >
            v2 · Cyan
          </span>
          <Link
            to="/styleguide-v3"
            className="px-4 py-1.5 rounded-full transition"
            style={{ color: "#64748b" }}
            data-testid="styleguide-toggle-v3"
          >
            v3 · Quiet
          </Link>
        </div>
      </div>

      {/* ─── Glass navigation (desktop) ─── */}
      <nav
        className="hidden md:flex items-center justify-between px-8 py-3 sticky top-0 z-10"
        style={{
          background: "rgba(13,18,29,0.72)",
          backdropFilter: "blur(18px)",
          borderBottom: "1px solid #1b2738",
        }}
      >
        <div className="flex items-center gap-8">
          <Logo />
          <div className="flex items-center gap-1">
            {NAV.map(({ label, icon: Icon, active }) => (
              <button
                key={label}
                className="relative flex items-center gap-2 px-4 py-2 rounded-md text-sm transition"
                style={{
                  color: active ? "#f8fafc" : "#64748b",
                  fontFamily: '"Plus Jakarta Sans",sans-serif',
                  fontWeight: active ? 600 : 500,
                }}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
                {active && (
                  <span
                    className="absolute -bottom-[13px] left-3 right-3 h-[2px] rounded-full"
                    style={{
                      background: "#0ea5e9",
                      boxShadow: "0 0 10px #0ea5e9, 0 0 20px rgba(14,165,233,0.5)",
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            className="h-8 w-8 rounded-md flex items-center justify-center transition hover:border-cyan-500/50"
            style={{ background: "#0d121d", border: "1px solid #1b2738" }}
          >
            <Search className="h-4 w-4" style={{ color: "#64748b" }} />
          </button>
          <div
            className="px-3 py-1.5 rounded-md text-xs flex items-center gap-2"
            style={{
              background: "rgba(16,185,129,0.08)",
              border: "1px solid rgba(16,185,129,0.3)",
              color: "#10b981",
              fontFamily: '"IBM Plex Mono",monospace',
              fontWeight: 500,
            }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full inline-block"
              style={{ background: "#10b981", boxShadow: "0 0 8px #10b981" }}
            />
            SYS&nbsp;LIVE
          </div>
          <div
            className="px-3 py-1.5 rounded-md text-xs flex items-center gap-2"
            style={{
              background: "#141b2a",
              border: "1px solid #1b2738",
              color: "#f8fafc",
              fontFamily: '"IBM Plex Mono",monospace',
              fontWeight: 500,
            }}
          >
            BAL&nbsp;$1,243.80
          </div>
          <div
            className="h-8 w-8 rounded-md flex items-center justify-center text-xs font-bold"
            style={{
              background: "linear-gradient(135deg,#0ea5e9,#0369a1)",
              color: "#06080d",
              fontFamily: '"Outfit",sans-serif',
            }}
          >
            NK
          </div>
        </div>
      </nav>

      {/* Mobile nav */}
      <nav
        className="md:hidden flex items-center justify-between px-4 py-3"
        style={{
          background: "rgba(13,18,29,0.72)",
          backdropFilter: "blur(14px)",
          borderBottom: "1px solid #1b2738",
        }}
      >
        <button className="p-2 -ml-2">
          <Menu className="h-5 w-5" style={{ color: "#f8fafc" }} />
        </button>
        <Logo compact />
        <div
          className="h-7 w-7 rounded-md flex items-center justify-center text-[11px] font-bold"
          style={{
            background: "linear-gradient(135deg,#0ea5e9,#0369a1)",
            color: "#06080d",
            fontFamily: '"Outfit",sans-serif',
          }}
        >
          NK
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8 md:py-12 space-y-16 pb-24">
        {/* ─── Hero ─── */}
        <section>
          <div
            className="text-xs uppercase inline-flex items-center gap-2 px-3 py-1 rounded-md"
            style={{
              color: "#0ea5e9",
              letterSpacing: "0.22em",
              fontFamily: '"IBM Plex Mono",monospace',
              background: "rgba(14,165,233,0.08)",
              border: "1px solid rgba(14,165,233,0.3)",
              fontWeight: 500,
            }}
          >
            <span
              className="h-1 w-1 rounded-full"
              style={{ background: "#0ea5e9", boxShadow: "0 0 6px #0ea5e9" }}
            />
            DESIGN PROPOSAL · v2
          </div>
          <h1
            className="mt-5 text-4xl md:text-6xl leading-[1.02]"
            style={{
              fontFamily: '"Outfit",sans-serif',
              fontWeight: 700,
              letterSpacing: "-0.03em",
            }}
          >
            Tactical, dense,<br />
            <span
              style={{
                background: "linear-gradient(90deg,#0ea5e9,#06b6d4 50%,#10b981)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              built for the desk.
            </span>
          </h1>
          <p
            className="mt-5 max-w-xl text-base md:text-lg"
            style={{ color: "#64748b" }}
          >
            Obsidian base with electric cyan + mint signal accents. Outfit for
            display, Plus Jakarta Sans for body, IBM Plex Mono for every number
            on screen. Glass headers, hairline edges, high-density rows — a
            trading-desk feel for an operator who lives in the data.
          </p>
        </section>

        {/* ─── Palette ─── */}
        <section>
          <SectionTitle number="01" title="Palette" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
            {PALETTE.map((c) => (
              <div
                key={c.name}
                className="rounded-md p-3 transition hover:-translate-y-0.5 duration-200"
                style={{ background: "#0d121d", border: "1px solid #1b2738" }}
              >
                <div
                  className="h-16 w-full rounded mb-3"
                  style={{
                    background: c.hex,
                    border: "1px solid rgba(255,255,255,0.04)",
                  }}
                />
                <div
                  className="text-sm"
                  style={{ fontFamily: '"Outfit",sans-serif', fontWeight: 600 }}
                >
                  {c.name}
                </div>
                <div
                  className="text-xs mt-0.5"
                  style={{
                    color: "#64748b",
                    fontFamily: '"IBM Plex Mono",monospace',
                  }}
                >
                  {c.hex}
                </div>
                <div className="text-xs mt-1" style={{ color: "#64748b" }}>
                  {c.role}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ─── Typography ─── */}
        <section>
          <SectionTitle number="02" title="Typography" />
          <div
            className="rounded-md p-6 md:p-10 mt-6 space-y-7"
            style={{ background: "#0d121d", border: "1px solid #1b2738" }}
          >
            <TypeRow
              hint='Display · Outfit 56 / -3%'
              render={
                <div
                  className="text-5xl md:text-6xl"
                  style={{
                    fontFamily: '"Outfit",sans-serif',
                    fontWeight: 700,
                    letterSpacing: "-0.03em",
                  }}
                >
                  Hit rate: 63.2%
                </div>
              }
            />
            <TypeRow
              hint='Heading · Outfit 24 / -1%'
              render={
                <div
                  className="text-2xl"
                  style={{
                    fontFamily: '"Outfit",sans-serif',
                    fontWeight: 600,
                    letterSpacing: "-0.015em",
                  }}
                >
                  Top-20 Elite · tonight
                </div>
              }
            />
            <TypeRow
              hint='Body · Plus Jakarta Sans 16'
              render={
                <div
                  className="text-base max-w-xl"
                  style={{ fontFamily: '"Plus Jakarta Sans",sans-serif' }}
                >
                  Headlines sit high and tight. Body stays warm but neutral.
                  Numbers always use IBM Plex Mono so columns of odds, stakes
                  and ROI align to the pixel.
                </div>
              }
            />
            <TypeRow
              hint='Numeric · IBM Plex Mono 20 · tabular'
              render={
                <div
                  className="text-xl flex gap-6 items-end"
                  style={{
                    fontFamily: '"IBM Plex Mono",monospace',
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  <span style={{ color: "#10b981" }}>+18.42%</span>
                  <span style={{ color: "#64748b" }}>|</span>
                  <span>1.72</span>
                  <span style={{ color: "#64748b" }}>|</span>
                  <span style={{ color: "#ff3b30" }}>−20.00</span>
                </div>
              }
            />
          </div>
        </section>

        {/* ─── Match / pick card ─── */}
        <section>
          <SectionTitle number="03" title="Match card · pick row" />
          <div className="grid gap-3 mt-6">
            {PICKS.map((p) => (
              <PickCard key={`${p.home}-${p.away}`} p={p} />
            ))}
          </div>
          <div
            className="text-xs mt-3"
            style={{ color: "#64748b" }}
          >
            Dense three-column layout — kickoff + league on the left, matchup
            + market pill in the middle, confidence meter + odds delta on the
            right. One hairline border, one glowing top edge on hover.
          </div>
        </section>

        {/* ─── Data table ─── */}
        <section>
          <SectionTitle number="04" title="Data table · recent bets" />
          <div
            className="rounded-md mt-6 overflow-hidden"
            style={{ background: "#0d121d", border: "1px solid #1b2738" }}
          >
            <table
              className="w-full text-sm"
              style={{
                fontFamily: '"IBM Plex Mono",monospace',
                fontVariantNumeric: "tabular-nums",
              }}
            >
              <thead>
                <tr
                  style={{
                    background: "#141b2a",
                    color: "#64748b",
                    borderBottom: "1px solid #1b2738",
                  }}
                  className="text-xs uppercase tracking-wider text-left"
                >
                  <th className="px-4 py-2.5 font-medium">T</th>
                  <th className="px-4 py-2.5 font-medium">Match</th>
                  <th className="px-4 py-2.5 font-medium">Market</th>
                  <th className="px-4 py-2.5 font-medium text-right">Stake</th>
                  <th className="px-4 py-2.5 font-medium text-right">Odds</th>
                  <th className="px-4 py-2.5 font-medium text-right">P&amp;L</th>
                  <th className="px-4 py-2.5 font-medium text-right">State</th>
                </tr>
              </thead>
              <tbody>
                {TABLE_ROWS.map((r, i) => {
                  const sc = STATE_COLORS[r.state];
                  const pnlPos = r.pnl > 0;
                  const pnlZero = r.pnl === 0;
                  return (
                    <tr
                      key={i}
                      className="transition"
                      style={{
                        background: i % 2 === 0 ? "transparent" : "rgba(20,27,42,0.4)",
                        borderBottom: "1px solid #1b2738",
                      }}
                    >
                      <td className="px-4 py-3" style={{ color: "#64748b" }}>{r.t}</td>
                      <td className="px-4 py-3" style={{ color: "#f8fafc", fontFamily: '"Plus Jakarta Sans",sans-serif' }}>{r.pair}</td>
                      <td className="px-4 py-3" style={{ color: "#64748b", fontFamily: '"Plus Jakarta Sans",sans-serif' }}>{r.mkt}</td>
                      <td className="px-4 py-3 text-right">${r.stake.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right">{r.odds.toFixed(2)}</td>
                      <td
                        className="px-4 py-3 text-right"
                        style={{
                          color: pnlZero ? "#64748b" : (pnlPos ? "#10b981" : "#ff3b30"),
                        }}
                      >
                        {pnlZero ? "±0.00" : (pnlPos ? "+" : "") + r.pnl.toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] uppercase tracking-wider"
                          style={{
                            background: sc.bg,
                            border: `1px solid ${sc.bd}`,
                            color: sc.fg,
                          }}
                        >
                          {r.state}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* ─── Stat tiles ─── */}
        <section>
          <SectionTitle number="05" title="Metric tiles · dashboard" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {TILES.map((t) => (
              <StatTile key={t.label} t={t} />
            ))}
          </div>
        </section>

        {/* ─── Footer line ─── */}
        <section
          className="rounded-md p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4"
          style={{
            background:
              "linear-gradient(135deg, rgba(14,165,233,0.08), rgba(16,185,129,0.04))",
            border: "1px solid rgba(14,165,233,0.3)",
          }}
        >
          <div>
            <div
              className="text-xs uppercase tracking-widest"
              style={{ color: "#0ea5e9", fontFamily: '"IBM Plex Mono",monospace' }}
            >
              READY TO APPLY
            </div>
            <div
              className="mt-1 text-xl"
              style={{ fontFamily: '"Outfit",sans-serif', fontWeight: 600 }}
            >
              Approve v2 and I'll roll the Schedule page over to this look next.
            </div>
          </div>
          <Link
            to="/styleguide"
            className="px-4 py-2 rounded-md text-sm transition hover:opacity-80"
            style={{
              background: "transparent",
              border: "1px solid #1b2738",
              color: "#64748b",
              fontFamily: '"Plus Jakarta Sans",sans-serif',
              fontWeight: 500,
            }}
            data-testid="styleguide-footer-view-v1"
          >
            Compare with v1 →
          </Link>
        </section>
      </main>
    </div>
  );
}

/* ────────── Local sub-components ────────── */

function SectionTitle({ number, title }) {
  return (
    <div className="flex items-baseline gap-4">
      <span
        className="text-xs tracking-widest"
        style={{
          color: "#0ea5e9",
          fontFamily: '"IBM Plex Mono",monospace',
          fontWeight: 500,
        }}
      >
        {number}
      </span>
      <h2
        className="text-2xl"
        style={{
          fontFamily: '"Outfit",sans-serif',
          fontWeight: 600,
          letterSpacing: "-0.015em",
        }}
      >
        {title}
      </h2>
      <div
        className="flex-1 h-px"
        style={{ background: "#1b2738" }}
      />
    </div>
  );
}

function TypeRow({ hint, render }) {
  return (
    <div>
      <div
        className="text-xs mb-1.5"
        style={{
          color: "#64748b",
          fontFamily: '"IBM Plex Mono",monospace',
        }}
      >
        {hint}
      </div>
      {render}
    </div>
  );
}

function PickCard({ p }) {
  const confHigh = p.conf >= 75;
  const confMid = p.conf >= 65 && p.conf < 75;
  const confColor = confHigh ? "#10b981" : confMid ? "#0ea5e9" : "#f59e0b";
  const deltaPos = p.delta > 0;
  return (
    <div
      className="group relative rounded-md overflow-hidden transition hover:-translate-y-0.5 duration-200"
      style={{ background: "#0d121d", border: "1px solid #1b2738" }}
    >
      {/* Top glow edge on hover */}
      <div
        className="absolute inset-x-0 top-0 h-px opacity-0 group-hover:opacity-100 transition-opacity"
        style={{
          background:
            "linear-gradient(90deg, transparent, #0ea5e9, transparent)",
          boxShadow: "0 0 12px rgba(14,165,233,0.6)",
        }}
      />
      <div className="grid grid-cols-12 items-center gap-3 p-4">
        {/* LEFT · kickoff + league */}
        <div className="col-span-12 md:col-span-3 flex items-center gap-3">
          <div
            className="flex items-center justify-center h-11 w-11 rounded-md shrink-0"
            style={{
              background: "#141b2a",
              border: "1px solid #1b2738",
              fontFamily: '"IBM Plex Mono",monospace',
              fontWeight: 500,
            }}
          >
            <Clock className="h-4 w-4" style={{ color: "#64748b" }} />
          </div>
          <div>
            <div
              className="text-lg"
              style={{
                fontFamily: '"IBM Plex Mono",monospace',
                fontWeight: 500,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {p.ko}
            </div>
            <div
              className="text-xs"
              style={{ color: "#64748b" }}
            >
              {p.league}
            </div>
          </div>
        </div>

        {/* MIDDLE · match + pill */}
        <div className="col-span-12 md:col-span-6">
          <div
            className="text-sm md:text-base"
            style={{
              fontFamily: '"Outfit",sans-serif',
              fontWeight: 600,
              letterSpacing: "-0.01em",
            }}
          >
            {p.home} <span style={{ color: "#64748b" }}>vs</span> {p.away}
          </div>
          <div className="mt-1.5 flex items-center gap-2">
            <span
              className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded"
              style={{
                background: "rgba(100,116,139,0.1)",
                border: "1px solid #1b2738",
                color: "#64748b",
                fontFamily: '"IBM Plex Mono",monospace',
                fontWeight: 500,
              }}
            >
              {p.market}
            </span>
            <span
              className="text-xs uppercase px-2 py-0.5 rounded font-medium"
              style={{
                background: "rgba(14,165,233,0.14)",
                border: "1px solid rgba(14,165,233,0.35)",
                color: "#0ea5e9",
                fontFamily: '"IBM Plex Mono",monospace',
              }}
            >
              {p.pick}
            </span>
          </div>
        </div>

        {/* RIGHT · confidence + odds */}
        <div className="col-span-12 md:col-span-3 flex items-center justify-between md:justify-end gap-4">
          <div className="flex items-center gap-2">
            <div
              className="h-2 w-20 rounded-full overflow-hidden"
              style={{ background: "#141b2a", border: "1px solid #1b2738" }}
            >
              <div
                className="h-full"
                style={{
                  width: `${p.conf}%`,
                  background: confColor,
                  boxShadow: `0 0 6px ${confColor}`,
                }}
              />
            </div>
            <div
              className="text-sm w-10 text-right"
              style={{
                fontFamily: '"IBM Plex Mono",monospace',
                fontWeight: 500,
                color: confColor,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {p.conf.toFixed(1)}
            </div>
          </div>
          <div className="text-right">
            <div
              className="text-lg leading-none"
              style={{
                fontFamily: '"IBM Plex Mono",monospace',
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {p.odds.toFixed(2)}
            </div>
            <div
              className="text-xs mt-1 flex items-center gap-0.5 justify-end"
              style={{
                color: deltaPos ? "#10b981" : "#ff3b30",
                fontFamily: '"IBM Plex Mono",monospace',
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {deltaPos ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
              {deltaPos ? "+" : ""}{p.delta.toFixed(1)}%
            </div>
          </div>
          <ChevronRight
            className="h-4 w-4 hidden md:block"
            style={{ color: "#1b2738" }}
          />
        </div>
      </div>
    </div>
  );
}

function StatTile({ t }) {
  const Icon = t.icon;
  const trendPos = t.trend != null && t.trend > 0;
  const trendColor = t.trend == null ? "#64748b" : (trendPos ? "#10b981" : "#ff3b30");
  return (
    <div
      className="relative rounded-md p-4 overflow-hidden transition hover:-translate-y-0.5 duration-200"
      style={{ background: "#0d121d", border: "1px solid #1b2738" }}
    >
      <div
        className="absolute inset-x-0 top-0 h-px"
        style={{
          background: "linear-gradient(90deg, transparent, #0ea5e9, transparent)",
          opacity: 0.5,
        }}
      />
      <div className="flex items-start justify-between">
        <div
          className="text-xs uppercase tracking-wider"
          style={{
            color: "#64748b",
            fontFamily: '"IBM Plex Mono",monospace',
            fontWeight: 500,
          }}
        >
          {t.label}
        </div>
        <div
          className="h-7 w-7 rounded flex items-center justify-center"
          style={{
            background: "#141b2a",
            border: "1px solid #1b2738",
          }}
        >
          <Icon className="h-3.5 w-3.5" style={{ color: "#0ea5e9" }} />
        </div>
      </div>
      <div
        className="mt-3 text-3xl"
        style={{
          fontFamily: '"IBM Plex Mono",monospace',
          fontWeight: 600,
          fontVariantNumeric: "tabular-nums",
          letterSpacing: "-0.02em",
        }}
      >
        {t.value}
      </div>
      {t.trend != null && (
        <div
          className="mt-1 text-xs flex items-center gap-1"
          style={{
            color: trendColor,
            fontFamily: '"IBM Plex Mono",monospace',
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {trendPos ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
          {trendPos ? "+" : ""}{t.trend}
          <span style={{ color: "#64748b" }}>vs 7d</span>
        </div>
      )}
    </div>
  );
}

function Logo({ compact = false }) {
  return (
    <div className="flex items-center gap-2">
      <div
        className="relative h-7 w-7 rounded flex items-center justify-center"
        style={{
          background: "linear-gradient(135deg,#0ea5e9,#06b6d4)",
          boxShadow: "0 0 12px rgba(14,165,233,0.5)",
        }}
      >
        <BarChart3 className="h-4 w-4" style={{ color: "#06080d" }} />
      </div>
      {!compact && (
        <div
          className="text-base"
          style={{
            fontFamily: '"Outfit",sans-serif',
            fontWeight: 700,
            letterSpacing: "0.02em",
          }}
        >
          NDAKU<span style={{ color: "#0ea5e9" }}>BET</span>
        </div>
      )}
    </div>
  );
}
